import { useProject } from './ProjectContext';

/**
 * The analysis_results -> view-model derivation, lifted verbatim out of the old
 * ProjectDetailPage so every sub-route reads the same shapes.
 *
 * Kept byte-for-byte on purpose: the fallback chains encode which keys legacy
 * projects persisted (acas_blocks / part1_acas) and which defaults stand in for
 * a missing block. Re-deriving them by hand would silently change KPIs for
 * projects already in the database.
 *
 * Note: the backend flattens its `kpis` dict into the root of analysis_results
 * (dacams_task._serialize_analysis_results), so the root-level reads below are
 * the KPIs -- there is no `analysis_results.kpis` object to read instead.
 */
export function useProjectAnalysis() {
  const { project, adosaOverride } = useProject();

  const results: any = project.analysis_results || {};

  // Safe number formatter - handles null, undefined, strings, and non-numeric values
  const safeNum = (val: any, decimals: number = 1): string => {
    if (val === null || val === undefined) return '0';
    const num = typeof val === 'number' ? val : parseFloat(val);
    if (isNaN(num)) return '0';
    return num.toFixed(decimals);
  };

  // APCAS KPIs - estructura del nuevo backend con parts y apcas_blocks
  const parts = results.parts || {};
  // Backward-compat: legacy projects persisted acas_blocks / part1_acas keys.
  const apcasBlocks = results.apcas_blocks || parts.part1_apcas || (results as any).acas_blocks || parts.part1_acas || {};
  const part3 = parts.part3_equilibrium || {};
  const part4 = parts.part4_indicators || {};
  const part5 = parts.part5_kpis || {};
  const part2 = parts.part2_adosa || {};

  // Block 1: Baseline & Intensity (D-FOL inputs)
  const block1 = apcasBlocks.block1 || {};
  // Block 2: Complexity
  const block2 = apcasBlocks.block2 || {};
  // Block 3: Efficiency
  const block3 = apcasBlocks.block3 || {};
  // Block 4: Capacity
  const block4 = apcasBlocks.block4 || {};
  // Block 5: Saturation
  const block5 = apcasBlocks.block5 || {};
  // Block 6: Management
  const block6 = apcasBlocks.block6 || {};
  // Block 7: Equilibrium Inputs
  const block7 = apcasBlocks.block7 || {};

  // Backward compatibility - flat structure
  const apcas = {
    // Block 1: Baseline
    pht_horario_realista: block1.pht || results.pht_horario_realista || 0,
    n_dynamic: block1.n_dynamic || results.n_dynamic || 0,
    n_dynamic_check: block1.n_dynamic_check ?? results.n_dynamic_check ?? null,
    cli: block1.cli ?? results.cli ?? null,
    w_workload: block1.w_workload || results.w_workload || 0,
    t_sector: block1.t_sector || 12.5,
    am_index: block1.am_index || 1.08,
    cmp_infra: block4.cmp_infra || results.cmp_infra || 0,
    sc_controller: block1.sc_controller || results.sc_controller || 0,
    oif: block1.oif || results.oif || 0,

    // Block 2-3: Complexity & Efficiency
    tcf: block2.tcf || 0,
    wcf: block2.wcf || 0,
    wif: block2.wif || 0,
    ccf: block2.ccf || 0,
    nof: block2.nof || 0,
    cf_complexity: block2.cf_total || results.cf_complexity || 0,
    ef_factor: block3.ef_factor || 1.35,
    acdm_factor: block3.acdm_factor || 1.0,
    efficiency_product: block3.efficiency_product || 1.35,
    aci_adj: block4.aci_adj || results.aci_adj || 0,

    // Block 4: RAC
    rac_capacity: block4.rac_capacity || results.rac_capacity || 0,
    ruig: block4.ruig || results.ruig || 0,
    n_stands: block4.n_stands || (project?.sme_parameters?.aip_data?.stands?.length) || 20,
    foc_factor: block4.foc_factor || 0.91,

    // Block 5: Saturation
    t_max: block5.t_max || 15,
    nss_saturation: block5.nss_nominal || results.nss_saturation || 0,
    nss_pct: block5.nss_pct || (results.nss_saturation || 0) * 100,
    cwl_norm: block5.cwl_norm || 0,
    nss_adjusted: block5.nss_adjusted || 0,

    // Block 6: NOP
    ctr: block6.ctr || 0,
    cwl_target: block6.cwl_target || 0.80,
    f_transition: block6.f_transition || 0.95,
    capacidad_util_pos: block6.capacidad_util_pos || 0,
    capacidad_util_adj: block6.capacidad_util_adj || 0,
    nop_required: block6.nop_required || results.nop_required || 0,
    nop_max: block6.nop_max || 0,

    // Block 7: Equilibrium inputs
    rrc_input: block7.rrc_input || 30,
    ptbc_input: block7.ptbc_input || 17.4,

    // Part 3: Equilibrium
    controlling_capacity: part3.controlling_capacity || results.controlling_capacity || 0,
    mismatch_rwy: part3.mismatch_rwy || 0,
    mismatch_apron: part3.mismatch_apron || 0,
    mismatch_terminal: part3.mismatch_terminal || 0,
    bci_index: part3.bci || results.bci_index || 0,
    svs_score: part3.svs || results.svs || 0,
    svs_pct: part3.svs_pct || 0,
    rgpp: part3.rgpp || 0,

    // Part 4: Strategic Indicators
    is_schedule_unsustainable: part4.is_schedule_unsustainable || false,
    is_system_imbalanced: part4.is_system_imbalanced || false,
    is_apron_the_limiter: part4.is_apron_the_limiter || false,

    // Part 5: Strategic KPIs
    amsri_index: part5.amsri || results.amsri || 0,
    amsri_pct: part5.amsri_pct || 0,
    ips_index: part5.ips || results.ips || 0,
    ips_pct: part5.ips_pct || 0,
    amsri_action: part5.amsri_action || results.amsri_action || '',
    ips_priority: part5.ips_priority || '',
    capacity_alignment_index: part5.capacity_alignment_index ?? results.capacity_alignment_index ?? null,
    demand_component: part5.demand_component ?? results.demand_component ?? null,
    tech_gap_component: part5.tech_gap_component ?? results.tech_gap_component ?? null,

    // Nested blocks for compatibility with Heatmaps tab
    block2: {
      tcf: block2.tcf || 0,
      wcf: block2.wcf || 0,
      wif: block2.wif || 0,
      nof: block2.nof || 0,
      ccf: block2.ccf || 0,
      cf_total: block2.cf_total || 1.0,
      weights: block2.weights || { tcf: 0.35, wcf: 0.25, wif: 0.20, nof: 0.10, ccf: 0.10 },
      layer2: block2.layer2 || { available: false }
    },
    block4: {
      n_stands: block4.n_stands || (project?.sme_parameters?.aip_data?.stands?.length) || 20,
      cmp_infra: block4.cmp_infra || 0,
      rac_capacity: block4.rac_capacity || 0
    }
  };

  // ADOSA KPIs - from results + extended from part2_adosa
  const adosa = {
    dsr_real: part2.dsr_real || results.dsr_real || 0,
    dsr_theoretical: part2.dsr_theoretical || 0,
    risk_free_staffing: part2.risk_free_staffing || results.risk_free_staffing || 0,
    optimal_staffing: part2.optimal_staffing || results.optimal_staffing || 0,
    minimum_staffing: part2.minimum_staffing || results.minimum_staffing || 0,
    // Extended: Intermediate calculations
    net_duty_days: part2.net_duty_days || 0,
    available_working_days: part2.available_working_days || 0,
    yearly_working_days: part2.yearly_working_days || 0,
    absent_days: part2.absent_days || 0,
    training_days: part2.training_days || 0,
    safety_correction_index: part2.safety_correction_index || 0,
    safety_index_abs: part2.safety_index_abs || 0,
    safety_index_training: part2.safety_index_training || 0,
    // Extended: Pattern
    cycle_length: part2.cycle_length || 10,
    cycles_per_year: part2.cycles_per_year || 0,
    roster_work_days: part2.roster_work_days || 6,
    roster_off_days: part2.roster_off_days || 4,
    // Extended: 5-Scenario
    min_staff_no_safety: part2.min_staff_no_safety || 0,
    optimal_staff_no_safety: part2.optimal_staff_no_safety || 0,
    min_staff_with_safety: part2.min_staff_with_safety || 0,
    optimal_staff_with_safety: part2.optimal_staff_with_safety || 0,
    risk_free_staff: part2.risk_free_staff || 0,
    headcount_for_roster: part2.headcount_for_roster || 0,
    // Roster data
    monthly_roster: part2.monthly_roster || {},
    annual_roster: part2.annual_roster || {},
    // Staff gap
    staff_gap: part2.staff_gap || 0,
    is_understaffed: part2.is_understaffed || false,
    // Client-side recalculation overrides (if any)
    ...(adosaOverride || {}),
  };



  // demand_curve viene en analysis_results con formato [{label, hour, demand, capacity}]
  const rawDemandCurve = results.demand_curve || results.dataframes?.demand_profile || (project as any).demand_curve;
  const demandCurve = Array.isArray(rawDemandCurve) ? rawDemandCurve : [];
  const nopHourly: number[] = Array.isArray(results.nop_hourly) ? results.nop_hourly : [];
  const iata_busy_day_str: string | null = results.iata_busy_day || null;
  const capacidadUtilAdj: number = (apcasBlocks as any)?.block6?.capacidad_util_adj || 0;
  const chartData = Array.isArray(demandCurve) && demandCurve.length > 0
    ? demandCurve.map((item: any, i: number) => {
      // Handle both new format (object with hour/demand/capacity) and legacy
      if (typeof item === 'object' && item.hour) {
        return {
          hour: item.hour || item.label || `${String(i).padStart(2, '0')}:00`,
          demand: item.demand || 0,
          capacity: item.capacity || apcas.rac_capacity || 0
        };
      }
      // Legacy: simple array of numbers
      const val = typeof item === 'number' ? item : (item.demand || item.count || 0);
      return {
        hour: `${String(i).padStart(2, '0')}:00`,
        demand: val,
        capacity: apcas.rac_capacity || 0
      };
    })
    : Array.from({ length: 24 }, (_, i) => ({
      hour: `${String(i).padStart(2, '0')}:00`,
      demand: 0,
      capacity: apcas.rac_capacity || 0
    }));

  // Movement Distribution Chart Data (from advanced_analysis)
  // SAFE INITIALIZATION: Check if labels exist to avoid crash
  const depDist = results.advanced_analysis?.charts?.departure_distribution || { labels: [], values: [], arrivals: [], total: [] };
  const depDistLabels = Array.isArray(depDist.labels) ? depDist.labels : [];
  const depDistData = depDistLabels.map((label: string, i: number) => ({
    time: label,
    departures: depDist.values ? (depDist.values[i] || 0) : 0,
    arrivals: depDist.arrivals ? (depDist.arrivals[i] || 0) : 0,
    total: depDist.total ? (depDist.total[i] || 0) : 0,
  }));

  // Bank Analysis Chart Data (from advanced_analysis)
  const bankData = results.advanced_analysis?.charts?.bank_analysis || { labels: [], arrivals: [], departures: [], arr_banks: [], dep_banks: [] };
  const bankLabels = Array.isArray(bankData.labels) ? bankData.labels : [];
  const bankChartData = bankLabels.map((label: string, i: number) => ({
    time: label,
    arrivals: bankData.arrivals ? (bankData.arrivals[i] || 0) : 0,
    departures: bankData.departures ? (bankData.departures[i] || 0) : 0,
  }));

  return {
    results,
    provenance: results.provenance || {},
    safeNum,
    parts,
    apcasBlocks,
    part3,
    part4,
    part5,
    part2,
    block1,
    block2,
    block3,
    block4,
    block5,
    block6,
    block7,
    apcas,
    adosa,
    demandCurve,
    nopHourly,
    iataBusyDayStr: iata_busy_day_str,
    capacidadUtilAdj,
    chartData,
    depDist,
    depDistData,
    bankData,
    bankChartData,
  };
}
